import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken, getToken } from "@/lib/api";
import { Loader2, LogOut, RefreshCw, Briefcase, Mail, Newspaper, Clock, CheckCircle2, AlertTriangle } from "lucide-react";

export default function AdminInsights() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setAdmin(me.data);
        await load();
      } catch {
        setToken(null);
        navigate("/admin/login");
      }
    })();
  }, [navigate]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/insights/stats");
      setStats(data);
    } finally {
      setLoading(false);
    }
  }

  async function refresh() {
    setRefreshing(true);
    setRefreshMsg("Queued — fetching from NewsData.io…");
    try {
      const { data } = await api.post("/admin/insights/refresh");
      if (data.status === "already-running") {
        setRefreshMsg("A refresh is already in progress — please wait.");
        await pollJob(data.job_id);
      } else {
        await pollJob(data.job_id);
      }
      await load();
    } catch (e) {
      setRefreshMsg(e.response?.data?.detail || e.message || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  }

  async function pollJob(jobId, attempts = 30) {
    for (let i = 0; i < attempts; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const { data: job } = await api.get(`/admin/insights/jobs/${jobId}`);
        if (job.status === "done" || job.status === "partial") {
          const t = job.totals || {};
          setRefreshMsg(
            `Fetched ${t.received || 0} articles (${t.saved || 0} stored, ${t.skipped || 0} skipped${job.status === "partial" ? " — partial failure" : ""}).`,
          );
          return;
        }
        setRefreshMsg(`${job.status === "running" ? "Running" : "Queued"}… (${i + 1}/${attempts})`);
      } catch (e) {
        setRefreshMsg(`Polling failed: ${e.message}`);
        return;
      }
    }
    setRefreshMsg("Job took longer than expected — check Recent Fetches for status.");
  }

  function logout() {
    setToken(null);
    navigate("/admin/login");
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 grid grid-cols-1 lg:grid-cols-[260px_1fr]" data-testid="admin-insights-page">
      <aside className="border-r border-white/5 bg-black/40 px-5 py-6 hidden lg:flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="h-7 w-7 rounded-sm bg-gradient-to-br from-blue-600 to-blue-500 grid place-items-center">
            <span className="font-display font-bold text-white text-sm">V</span>
          </div>
          <div>
            <div className="font-display font-bold tracking-tight text-[14px]">VYR Trade</div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-zinc-500">ADMIN CONSOLE</div>
          </div>
        </Link>

        <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-3">VIEWS</div>
        <div className="flex flex-col gap-1 mb-6">
          <Link to="/admin" className="px-3 py-2 rounded-md text-[13px] flex items-center gap-2 text-zinc-400 hover:bg-white/5 hover:text-white border border-transparent" data-testid="nav-enquiries">
            <Briefcase className="h-3.5 w-3.5" /> Sourcing Enquiries
          </Link>
          <Link to="/admin/messages" className="px-3 py-2 rounded-md text-[13px] flex items-center gap-2 text-zinc-400 hover:bg-white/5 hover:text-white border border-transparent" data-testid="nav-messages">
            <Mail className="h-3.5 w-3.5" /> Contact Messages
          </Link>
          <Link to="/admin/insights" className="px-3 py-2 rounded-md text-[13px] flex items-center gap-2 border bg-zinc-900 border-white/10 text-white" data-testid="nav-insights">
            <Newspaper className="h-3.5 w-3.5" /> Insights
          </Link>
        </div>

        <div className="mt-auto pt-6 border-t border-white/5">
          <div className="text-[12px] text-zinc-400 break-all">{admin?.email}</div>
          <button onClick={logout} className="mt-3 inline-flex items-center gap-2 text-[12px] text-zinc-400 hover:text-white" data-testid="admin-logout">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <section className="min-w-0">
        <div className="border-b border-white/5 bg-zinc-900/40 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-amber-400">INSIGHTS · NEWS PIPELINE</div>
            <div className="font-display text-2xl font-semibold mt-1">
              {stats?.total ?? "…"} <span className="text-zinc-500 text-base font-normal">articles</span>
            </div>
          </div>
          <button
            onClick={refresh}
            disabled={refreshing || !stats?.configured}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-60"
            data-testid="insights-refresh"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {refreshing ? "Fetching…" : "Refresh now"}
          </button>
        </div>

        {!stats?.configured && (
          <div className="m-6 border border-amber-400/30 bg-amber-400/[0.05] rounded-md p-4 flex items-start gap-3" data-testid="key-missing">
            <AlertTriangle className="h-4 w-4 text-amber-300 mt-0.5" />
            <div className="text-[13px] text-amber-200">
              NEWS_API_KEY is not configured on the server. Set it in <code>/app/backend/.env</code> and restart the backend to enable news ingestion.
            </div>
          </div>
        )}

        {refreshMsg && (
          <div className="m-6 mb-0 border border-white/10 bg-zinc-900/60 rounded-md px-4 py-3 text-[13px] text-zinc-200 flex items-center gap-2" data-testid="refresh-msg">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" /> {refreshMsg}
          </div>
        )}

        <div className="p-6 grid gap-6">
          {/* Category KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cat-kpis">
            {Object.entries(stats?.labels || {}).map(([slug, label]) => (
              <div key={slug} className="border border-white/5 bg-zinc-900/40 rounded-lg p-5">
                <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500">{label.toUpperCase()}</div>
                <div className="mt-1 font-display text-3xl font-bold tnum">{stats?.by_category?.[slug] || 0}</div>
                <div className="mt-1 text-[12px] text-zinc-500">articles stored · never deleted</div>
              </div>
            ))}
          </div>

          {/* Recent runs */}
          <div className="border border-white/5 bg-zinc-900/40 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" />
              <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-400">RECENT FETCHES</div>
            </div>
            {loading ? (
              <div className="px-5 py-8 text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
            ) : stats?.recent_runs?.length ? (
              <table className="w-full text-[13px]">
                <thead className="bg-zinc-950/60 text-zinc-500 font-mono text-[11px] tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5">TIME (UTC)</th>
                    <th className="text-left px-4 py-2.5">CATEGORY</th>
                    <th className="text-right px-4 py-2.5">RECEIVED</th>
                    <th className="text-right px-4 py-2.5">STORED</th>
                    <th className="text-right px-4 py-2.5">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_runs.map((r) => (
                    <tr key={r.id} className="border-t border-white/5">
                      <td className="px-4 py-2.5 font-mono text-zinc-400">{new Date(r.at).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-zinc-100">{stats.labels[r.category] || r.category}</td>
                      <td className="px-4 py-2.5 text-right tnum text-zinc-300">{r.received}</td>
                      <td className="px-4 py-2.5 text-right tnum text-emerald-300">{r.saved}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-mono border ${r.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
                          {r.ok ? "OK" : "FAIL"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-5 py-8 text-zinc-500 text-sm" data-testid="no-runs">No fetches yet. Hit &quot;Refresh now&quot; to ingest articles immediately.</div>
            )}
          </div>

          <div className="border border-white/5 bg-zinc-950 rounded-md p-4 text-[12px] text-zinc-400 leading-relaxed">
            <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-1">SCHEDULE</div>
            Automated fetch runs at <span className="font-mono text-zinc-200">09:00</span> and <span className="font-mono text-zinc-200">21:00 UTC</span> daily. Articles are deduped by URL+category and never deleted. NewsData.io free tier: 200 credits/day — current schedule uses ~6.
          </div>
        </div>
      </section>
    </div>
  );
}
